<template>
  <div>
    <!-- Overlay to dim the background -->
    <div class="overlay"></div>

    <!-- Popup content -->
    <div class="prev-board-popup custom-class" @click.self="$emit('close')">
      <span class="close-button material-icons-round" @click="$emit('close')"> close </span>
      <div class="header">
        <h2>Previous Boards</h2>
        <h5>Here are all the grids since April 7th 2024. Click on a grid to play.</h5>
      </div>
      <div class="calendar">
        <div class="calendar-header">
          <button @click="previousMonth" class="btn">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="rgb(223, 223, 223)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Previous
          </button>
          <span class="month-label">{{ currentMonth.format('MMMM YYYY') }}</span>
          <button @click="nextMonth" class="btn">
            Next
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 18L15 12L9 6"
                stroke="rgb(223, 223, 223)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
        <div class="calendar-grid">
          <div
            class="day-name"
            v-for="day in ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']"
            :key="day"
          >
            {{ day }}
          </div>
          <div
            v-for="(cell, index) in calendarGrid"
            :key="index"
            :class="{
              'calendar-cell': true,
              'has-data': cell?.board?.boardId,
              'outside-month': !cell.inCurrentMonth,
              today: isToday(cell),
            }"
            @click="cell.board && handleCellClick(cell.board.boardId)"
          >
            <p v-if="cell.date">{{ cell.date.date() }}</p>
            <span
              v-if="cell.board"
              :class="{ 'play-now': true, 'double-line': userData[cell.board.boardId] }"
            >
              {{
                userData[cell.board.boardId]
                  ? userData[cell.board.boardId].isGameOver
                    ? 'Score: ' + userData[cell.board.boardId].score
                    : userData[cell.board.boardId].hasMadeGuessInInfiniteMode
                      ? 'Continue'
                      : 'Guessed (' + (10 - userData[cell.board.boardId].guesses) + '/10)'
                  : '#' + cell.board.boardId
              }}
            </span>
          </div>
        </div>
        <button class="btn-secondary btn-container" @click="playRandomGrid">
          Play a random grid
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import dayjs from 'dayjs';
  import getBoardId, { getDateFromBoardId } from '@/utils/getBoardId';

  export default {
    data() {
      return {
        currentMonth: dayjs().startOf('month'),
        playableGrids: [],
        userData: {},
        selectedBoardId: null,
      };
    },
    mounted() {
      try {
        this.getBoards();
        this.getUserData();
      } catch (error) {
        console.error('Error fetching boards:', error);
      }
    },

    methods: {
      // Fetch the list of playable boards
      getBoards() {
        try {
          const todaysBoardId = getBoardId(dayjs().format('YYYY-MM-DD'));
          this.playableGrids = Array.from({ length: todaysBoardId })
            .map((_, i) => {
              const boardId = i + 1;
              return {
                boardId: boardId.toString(),
                date: getDateFromBoardId(boardId),
              };
            })
            .sort((a, b) => b.boardId - a.boardId);
        } catch (error) {
          console.error('Error fetching boards:', error);
        }
      },

      // Get the user data from local storage
      getUserData() {
        const userDataString = localStorage.getItem('userData');
        if (userDataString) {
          this.userData = JSON.parse(userDataString);
        }
      },

      isToday(cell) {
        return cell?.date?.isSame(dayjs(), 'day');
      },

      playRandomGrid() {
        const validBoards = this.playableGrids;
        if (!validBoards.length) return;

        const randomIndex = Math.floor(Math.random() * validBoards.length);
        const randomBoard = validBoards[randomIndex];

        if (randomBoard && randomBoard.boardId) {
          this.$router.push(`/board/${randomBoard.boardId}`);
          this.$emit('select-board', randomBoard.boardId);
          this.$emit('close');
        }
      },
      handleCellClick(boardId) {
        this.selectedBoardId = boardId;
        this.$emit('select-board', boardId);
        this.$router.push(`/board/${boardId}`);
      },

      previousMonth() {
        const minDate = dayjs('2024-04-01');
        if (this.currentMonth.isAfter(minDate, 'month')) {
          this.currentMonth = this.currentMonth.subtract(1, 'month');
        }
      },

      nextMonth() {
        const now = dayjs();
        if (this.currentMonth.isBefore(now, 'month')) {
          this.currentMonth = this.currentMonth.add(1, 'month');
        }
      },
    },
    computed: {
      calendarGrid() {
        const startOfMonth = this.currentMonth.startOf('month');
        const endOfMonth = this.currentMonth.endOf('month');

        const firstDow = startOfMonth.day(); // 0 = Dom
        const daysInMonth = endOfMonth.date();
        const TOTAL_CELLS = 42; // 6 semanas

        const preDays = firstDow; // dias do mês anterior no início
        const postDays = TOTAL_CELLS - preDays - daysInMonth; // dias do próximo mês no fim

        const grid = [];

        for (let i = preDays; i > 0; i--) {
          const date = startOfMonth.subtract(i, 'day');
          const board = this.playableGrids.find(
            (g) => g.date.format('YYYY-MM-DD') === date.format('YYYY-MM-DD')
          );
          grid.push({ date, board, inCurrentMonth: false });
        }

        for (let i = 1; i <= daysInMonth; i++) {
          const date = startOfMonth.date(i);
          const board = this.playableGrids.find(
            (g) => g.date.format('YYYY-MM-DD') === date.format('YYYY-MM-DD')
          );
          grid.push({ date, board, inCurrentMonth: true });
        }

        for (let i = 1; i <= postDays; i++) {
          const date = endOfMonth.add(i, 'day');
          const board = this.playableGrids.find(
            (g) => g.date.format('YYYY-MM-DD') === date.format('YYYY-MM-DD')
          );
          grid.push({ date, board, inCurrentMonth: false });
        }

        return grid;
      },
    },
  };
</script>

<style scoped>
  .overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9998;
  }

  .prev-board-popup {
    position: fixed;
    width: 70%;
    max-width: 1200px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: rgb(53, 53, 72);
    padding: 32px;
    border-radius: 16px;
    z-index: 9999;
    color: #fff;
  }

  .header {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .header h2 {
    font-size: 28px;
    margin-bottom: 0;
  }

  .header h5 {
    font-size: 16px;
    margin-top: 0;
    font-weight: 400;
  }

  .calendar {
    display: flex;
    flex-direction: column;
  }

  .calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    border-left: 1px solid rgba(255, 255, 255, 0.2);
    border-bottom-left-radius: 16px;
  }

  .today {
    box-shadow: inset 0 0 0 2px rgba(147, 147, 236, 1);
    z-index: 1;
    position: relative;
  }

  .day-name {
    font-weight: bold;
    text-align: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    border-right: 1px solid rgba(255, 255, 255, 0.2);
    padding: 12px;
  }

  .outside-month {
    opacity: 0.5;
  }
  .calendar-cell.has-data {
    cursor: pointer;
  }
  .calendar-cell:not(.has-data) {
    cursor: default;
  }

  .btn-container {
    align-items: center;
    width: 100%;
    max-width: 295px;
    margin-top: 12px;
    margin-left: auto;
    margin-right: auto;
  }

  .btn {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 2px;
    background-color: transparent;
    border: none;
    cursor: pointer;
    font-size: 16px;
    color: rgb(223, 223, 223);
  }

  .calendar-cell {
    position: relative;
    cursor: pointer;
    height: 50px;
    padding: 4px;
    text-align: right;
    color: rgb(223, 223, 223);
    font-size: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-right: 1px solid rgba(255, 255, 255, 0.2);
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  }

  .calendar-cell:nth-last-child(7) {
    border-bottom-left-radius: 16px;
  }

  .calendar-cell:last-child {
    border-bottom-right-radius: 16px;
  }

  .calendar-cell:hover {
    background-color: rgba(42, 42, 58, 1);
  }

  .calendar-cell p {
    position: absolute;
    top: 4px;
    right: 6px;
    margin: 0;
    font-size: 12px;
    line-height: 1;
    pointer-events: none;
  }

  .has-data {
    background-color: rgba(76, 76, 103, 1);
  }

  .calendar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 16px;
    font-weight: bold;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    padding: 12px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: rgb(223, 223, 223);
  }

  .play-now {
    font-size: 18px;
    font-weight: bold;
    color: rgb(223, 223, 223);
    text-decoration: none;
    display: inline-block;
  }

  .close-button {
    position: absolute;
    top: 32px;
    right: 32px;
    cursor: pointer;
    font-size: 26px;
    color: rgb(223, 223, 223);
  }

  .close-button::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background-color: transparent;
    z-index: -1;
  }

  @media screen and (max-width: 850px) {
    .prev-board-popup {
      width: 70%;
      font-size: 14px;
      padding: 18px;
    }
    .play-now {
      font-size: 16px;
      line-height: 1;
      text-align: center;
    }
    .double-line {
      font-size: 14px;
      text-align: center;
    }
  }

  @media screen and (max-width: 480px) {
    .prev-board-popup {
      width: 90%;
      font-size: 12px;
      padding: 12px;
    }
    .header h2 {
      font-size: 18px;
    }
    .header h5 {
      font-size: 0.75rem;
    }
    .close-button {
      top: 24px;
      right: 24px;
    }
    .calendar-cell p {
      top: 2px;
      right: 2px;
    }
    .btn {
      font-size: 14px;
    }
    .calendar-cell span {
      font-size: 12px;
    }
    .play-now {
      font-size: 14px;
      line-height: 1;
      text-align: center;
    }
  }
</style>
